import permanentShopPageQuery from 'queries/permanentShopPage.graphql';
import {
  PageProps,
  QueryPermanentShopPageArgs,
  QueryPermanentShopPageOutput,
} from 'types/types';
import { setGTMPageName } from '../util/tag-manager';
import {
  fetchGraphQl,
  parseQueryAST,
  isAbortError,
  isNotFound,
} from '../util/graphql';
import GiftVoucherShopPage from 'components/_pages/gift-vouchers';
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import { useCustomerContext, useStaffPreview } from 'hooks/customer';
import useErrorNotificationHandler from 'hooks/handle-errors';
import { useLazyQuery } from '@apollo/client';
import { useEffect } from 'react';
import Loading from 'components/loading';
import config from '../util/load-config';
import NotFoundPage from 'pages/404';
import { giftVoucherPageProps } from 'components/_pages/gift-vouchers/util';
import { prepMetaTags } from '../util/seo';
import { PageNamesEnum } from 'types/third-party/gtm';
import { logError } from '../util/error-logger';
import { cacheControl } from 'constants/cache-control';
import { GiftVouchersPage } from 'components/_pages/gift-vouchers/types';
import { logInvalidJSON } from '../util/log-invalid-json';

setGTMPageName(PageNamesEnum.giftVouchersPage);

const SHOP_ID = 'gift-vouchers';

interface GiftVouchersPageProps {
  giftVouchersPage?: GiftVouchersPage;
}

const ShopPageWrapper = ({
  giftVouchersPage: giftVouchersPageSSR,
}: GiftVouchersPageProps) => {
  const context = useCustomerContext();
  const mountErrors = useErrorNotificationHandler();

  const [previewShopPage, { loading, error, data }] = useLazyQuery<
    QueryPermanentShopPageOutput,
    QueryPermanentShopPageArgs
  >(permanentShopPageQuery, {
    context,
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
  });

  const hasPermanentProducts =
    data && data.permanentShopProducts && data.permanentShopProducts.length > 0;
  const giftVouchersPage = hasPermanentProducts
    ? giftVoucherPageProps(data)
    : giftVouchersPageSSR;

  useStaffPreview(({ date }) =>
    previewShopPage({
      variables: {
        date,
        shopId: SHOP_ID,
        previewShopId: SHOP_ID,
      },
    })
  );

  useEffect(() => {
    error && mountErrors([new Error('Preview failed')]);
  }, [error, mountErrors]);

  if (!giftVouchersPage) {
    return <NotFoundPage />;
  }

  return (
    <Loading iconTop={200} isLoading={loading} loadingText="Preview loading...">
      <GiftVoucherShopPage
        categories={giftVouchersPage.categories}
        giftVouchersItems={giftVouchersPage.items}
      />
    </Loading>
  );
};

export async function getServerSideProps({
  res,
}: GetServerSidePropsContext): Promise<
  GetServerSidePropsResult<PageProps & GiftVouchersPageProps>
> {
  try {
    const [query, operationName] = await parseQueryAST(
      permanentShopPageQuery,
      'permanentShopPage'
    );

    const { data, errors } = await fetchGraphQl<
      QueryPermanentShopPageOutput,
      QueryPermanentShopPageArgs
    >({
      operationName,
      query,
      variables: { shopId: SHOP_ID, previewShopId: SHOP_ID },
      logInvalidJSON,
    });

    // only fail if there is no data
    if (!data || !data.permanentShopProducts) {
      res.setHeader('cache-control', cacheControl.temporary);

      // log error and set status based on error and data
      if (errors && errors.length > 0) {
        const [firstError] = errors;

        if (isNotFound(firstError)) {
          res.statusCode = 404;
          logError(firstError.message, 'gift-vouchers.query');
          // return empty props
          return { props: {} };
        }
        res.statusCode = 500;
      } else {
        res.statusCode = 404;
        logError(
          `No data returned for the permanentShop: "${SHOP_ID}"`,
          'gift-vouchers.query'
        );
      }

      // return empty props so we can render a failure page
      return { props: {} };
    }

    res.setHeader('cache-control', cacheControl.dynamic);

    const [headerTaglineTitle, headerTaglineText] = data?.permanentShop
      ?.blurb || [
      config.headerTaglines[SHOP_ID]?.header,
      config.headerTaglines[SHOP_ID]?.text,
    ];

    const meta = config.seo.meta.pages['gift-vouchers'];

    return {
      props: {
        giftVouchersPage: giftVoucherPageProps(data),
        meta: {
          theme: 'giftvouchers',
          messages: data.messages || null,
        },
        layout: {
          selectedTopTab: SHOP_ID,
          hideClock: true,
          hasScrollButton: true,
          hasAppDownloadWidget: true,
          disableHeaderShrinking: true,
          moreProductsFooter: {
            enabled: true,
            message: 'Looking for deals?',
          },
          ...(headerTaglineTitle ? { headerTaglineTitle } : {}),
          ...(headerTaglineText ? { headerTaglineText } : {}),
          ...(meta ? { metaTags: prepMetaTags(meta) } : {}),
        },
      },
    };
  } catch (e) {
    res.statusCode = isAbortError(e) ? 503 : 500;
    res.setHeader('cache-control', cacheControl.temporary);

    logError(e, 'gift-vouchers.catch');

    // return empty props so we can render a failure page
    return { props: {} };
  }
}

export default ShopPageWrapper;
